import { STD_RANGES, STD_UPPER } from './ranges';

const norm = s =>
  String(s || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** Map catalog trade names / ids onto fixture slugs (tests / legacy only). */
const SLUG_ALIASES = {
  electrical: ['electrical', 'electric'],
  utility: ['utility', 'utilities', 'wet utility', 'site utilities'],
  mechanical: ['mechanical', 'hvac', 'hvac mechanical'],
  earthwork: ['earthwork', 'earthworks', 'sitework earthwork'],
  concrete: ['concrete'],
  masonry: ['masonry']
};

export const tradeTatSlug = trade => {
  const candidates = [trade?.tat_slug, trade?.trade_slug, trade?.trade_key, trade?.trade_id, trade?.trade_name]
    .filter(Boolean)
    .map(norm);
  for (const [slug, aliases] of Object.entries(SLUG_ALIASES)) {
    if (candidates.some(c => c === slug || aliases.includes(c) || aliases.some(a => c.includes(a)))) {
      return slug;
    }
  }
  return candidates[0] || null;
};

const formatRangeLabel = (lower, upper) => {
  if (upper == null || upper === Infinity) {
    return lower <= 1 ? '1+' : `${lower}+`;
  }
  if (lower == null || lower <= 1) return `≤${upper}`;
  return `${lower}–${upper}`;
};

const scopeNameById = trade => {
  const map = {};
  const push = sc => {
    const id = sc?.scope_id || sc?.id;
    if (id != null) map[String(id)] = sc.scope_name || sc.name || String(id);
  };
  (trade?.scopes || []).forEach(push);
  (trade?.sections || []).forEach(sec => (sec.scopes || sec.assigned_scopes || []).forEach(push));
  return map;
};

/**
 * API 9 — presets[] with nested tat on get_scopes.
 * Cards = distinct tat.id in first-appearance (ladder) order.
 */
const normalizePresetsWithTat = (trade, rawPresets) => {
  const sorted = [...rawPresets].sort((a, b) => (a.position || 0) - (b.position || 0));
  const rangeSrc = sorted[0]?.tat?.ranges || [];
  if (!rangeSrc.length) return null;

  const ranges = rangeSrc.map(r => formatRangeLabel(r.lower_bound, r.upper_bound));
  const upper = rangeSrc.map(r => (r.upper_bound == null ? Infinity : Number(r.upper_bound)));
  const names = scopeNameById(trade);

  const cards = [];
  const seenCard = new Map();
  sorted.forEach(p => {
    const tat = p.tat;
    if (!tat?.id) return;
    const id = String(tat.id);
    if (seenCard.has(id)) return;
    seenCard.set(id, cards.length);
    cards.push({
      id,
      name: tat.name || `Card ${cards.length + 1}`,
      tier: cards.length + 1,
      of: 0,
      // Lightest preset on this card (first in ladder) — used by Switch.
      linkedPresetId: String(p.id),
      versions: [
        {
          appliesFrom: tat.effective_from || null,
          customer: (tat.ranges || []).map(r => (r.customer_days == null ? null : Number(r.customer_days))),
          // API 9 never ships internal days on customer/staff catalog.
          internal: []
        }
      ]
    });
  });
  cards.forEach(c => {
    c.of = cards.length;
  });
  if (!cards.length) return null;

  const presets = sorted.map((p, i) => {
    const scopeIds = (p.preset_scopes?.scope_ids || []).map(String);
    const elementMap = {};
    (p.preset_scopes?.elements || []).forEach(e => {
      if (e?.id == null) return;
      elementMap[String(e.id)] = !!e.is_on;
    });
    const answersByModifierId = {};
    (p.preset_scopes?.answers || []).forEach(a => {
      if (a?.modifier_id == null) return;
      answersByModifierId[String(a.modifier_id)] = {
        selected_options: a.selected_options || [],
        text_value: a.text_value || ''
      };
    });
    const resolvesScopeIds = (p.resolves_on?.scope_ids || []).map(String);
    const resolvesAnswers = (p.resolves_on?.answers || []).map(a => ({
      modifierId: String(a.modifier_id),
      options: a.options || []
    }));

    const prev = sorted[i - 1];
    const prevCardId = prev?.tat?.id != null ? String(prev.tat.id) : null;
    const cardId = String(p.tat?.id || '');
    const cardChanged = !prev || prevCardId !== cardId;
    const prevScopeIds = new Set((prev?.preset_scopes?.scope_ids || []).map(String));
    const addsScopeIds = scopeIds.filter(id => !prevScopeIds.has(id));
    // Affects-turnaround tag only when this rung moves to a different card.
    const taggedScopeIds = cardChanged ? resolvesScopeIds : [];
    const addsItems = (cardChanged ? taggedScopeIds : addsScopeIds).map(id => names[id] || id);
    const addsAnswers = cardChanged
      ? resolvesAnswers.flatMap(a => a.options.map(o => String(o)))
      : [];

    return {
      id: String(p.id),
      name: p.name || `Preset ${i + 1}`,
      desc: p.description || p.desc || '',
      cardId,
      rung: p.position != null ? p.position : i + 1,
      scopeIds,
      elementMap,
      answersByModifierId,
      resolvesOn: { scopeIds: resolvesScopeIds, answers: resolvesAnswers },
      // Fixture-compat fields (unused when scopeIds present).
      items: scopeIds.map(id => names[id] || id),
      answers: {},
      tradeAnswers: {},
      requires: {
        anyItems: resolvesScopeIds.map(id => names[id] || id),
        anyOptions: resolvesAnswers.flatMap(a => a.options)
      },
      addsItems,
      addsAnswers
    };
  });

  const stdLike =
    ranges.length === STD_RANGES.length &&
    upper.every((u, i) => u === STD_UPPER[i] || (u === Infinity && STD_UPPER[i] === Infinity));

  return {
    id: tradeTatSlug(trade) || String(trade.trade_id),
    ranges,
    upper,
    cards,
    presets,
    singleCard: cards.length <= 1,
    customRanges: !stdLike,
    source: 'api'
  };
};

/** Legacy / older API shape (flat tat_cards + presets). Always ladder-sort presets. */
const normalizeLegacyTatConfig = trade => {
  const presetsRaw = trade?.presets || trade?.scope_presets || [];
  const cards = trade?.tat_cards || trade?.cards || [];
  if (!presetsRaw.length && !cards.length) return null;

  const ranges = trade?.tat_ranges || trade?.sheet_ranges || STD_RANGES;
  const upper =
    trade?.tat_upper_bounds ||
    ranges.map((label, i) => {
      if (typeof label === 'object' && label.upper != null) return label.upper;
      return STD_UPPER[i] ?? Infinity;
    });

  // Lightest first by position/rung — do not trust payload order.
  const presets = [...presetsRaw]
    .map((p, i) => ({
      p,
      i,
      key: p.position != null ? Number(p.position) : p.rung != null ? Number(p.rung) : i + 1
    }))
    .sort((a, b) => a.key - b.key || a.i - b.i)
    .map(x => x.p);

  const normCards = cards.map((c, i) => ({
    id: String(c.id || c.card_id || `card-${i + 1}`),
    name: c.name || c.card_name || `Card ${i + 1}`,
    tier: c.tier || i + 1,
    of: cards.length,
    linkedPresetId: c.linked_preset_id || c.linkedPresetId || null,
    versions: (
      c.versions || [
        {
          appliesFrom: c.applies_from || null,
          customer: c.customer || c.days || [],
          internal: c.internal || []
        }
      ]
    ).map(v => ({
      appliesFrom: v.appliesFrom || v.applies_from || null,
      customer: v.customer || v.customer_days || [],
      internal: v.internal || v.internal_days || []
    }))
  }));

  const normPresets = presets.map((p, i) => ({
    id: String(p.id || p.preset_id || `preset-${i + 1}`),
    name: p.name || p.short || p.title || `Preset ${i + 1}`,
    desc: p.desc || p.description || '',
    cardId: String(p.card_id || p.cardId || normCards[0]?.id || ''),
    rung: p.position != null ? Number(p.position) : p.rung != null ? Number(p.rung) : i + 1,
    items: p.items || p.item_names || [],
    answers: p.answers || p.item_answers || {},
    tradeAnswers: p.tradeAnswers || p.trade_answers || {},
    requires: p.requires || null,
    addsItems: p.addsItems || p.adds_items || [],
    addsAnswers: p.addsAnswers || p.adds_answers || []
  }));

  // Lightest preset on each card (ladder is already lightest-first).
  normCards.forEach(card => {
    if (!card.linkedPresetId) {
      const linked = normPresets.find(p => p.cardId === card.id);
      if (linked) card.linkedPresetId = linked.id;
    }
  });

  return {
    id: tradeTatSlug(trade) || String(trade.trade_id),
    ranges: ranges.map(r => (typeof r === 'string' ? r : r.label || r.name)),
    upper,
    cards: normCards,
    presets: normPresets,
    singleCard: normCards.length <= 1,
    customRanges: !!(trade?.custom_tat_ranges || trade?.tat_custom_ranges),
    source: 'api'
  };
};

/**
 * Normalize get_scopes trade TAT payload into the cfg used by resolve/apply.
 * API 9: presets always present; [] = no card / no blocks.
 */
export const normalizeApiTatConfig = trade => {
  if (!trade) return null;
  if (Array.isArray(trade.presets)) {
    if (!trade.presets.length) return null;
    if (trade.presets[0]?.tat) return normalizePresetsWithTat(trade, trade.presets);
  }
  return normalizeLegacyTatConfig(trade);
};

/**
 * Prefer BE-authored presets on the trade.
 * API 9: `presets` always present; [] = no TAT UI.
 * No local fixture fallback — missing key / empty array means no blocks.
 */
export const getTradeTatConfig = trade => {
  if (!trade) return null;
  return normalizeApiTatConfig(trade);
};
